/** Send from the operator's OWN WhatsApp — the web twin of the Nurture app's
 *  share sheet (ShareSequencer.kt). Same kit, same drafting, same log.
 *
 *  Each message opens the WhatsApp desktop app straight into the lead's chat
 *  with the text already typed (`whatsapp://send`). The browser can't see
 *  whether they pressed send, so we ask, and only a "yes" writes the activity —
 *  the same honesty rule as the phone. */
import React, { useEffect, useState } from 'react';
import { Lead } from '../../types';
import { activityApi } from '../../services/api';
import { shareApi, ShareKit, ShareStep } from '../../services/marketingApi';
import Sheet, { SheetLabel, errText } from './Sheet';

const msisdn = (phone: string) => {
  const d = phone.replace(/\D/g, '');
  return d.length === 10 ? `91${d}` : d;
};

const openChat = (phone: string, text?: string) => {
  const q = text ? `&text=${encodeURIComponent(text)}` : '';
  window.location.href = `whatsapp://send?phone=${msisdn(phone)}${q}`;
};

const stepText = (s: ShareStep) => (s.kind === 'attachment' ? s.caption : s.text) || '';

type Phase = 'ready' | 'awaiting' | 'confirming';

// A pick, in the order it was tapped — and that is the order it goes out. A
// second tap on the same thing removes it; re-ordering is untap, tap again.
type Pick = { kind: 'asset' | 'suggestion'; id: string };

const ShareSheet: React.FC<{
  lead: Lead; operatorName?: string | null; operatorEmail?: string | null; onClose: () => void; onLogged: () => void;
}> = ({
  lead, operatorName, operatorEmail, onClose, onLogged,
}) => {
  const [kit, setKit] = useState<ShareKit | null>(null);
  const [picked, setPicked] = useState<Pick[]>([]);
  const [hint, setHint] = useState('');
  const [steps, setSteps] = useState<ShareStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('ready');
  const [confirmed, setConfirmed] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One id per run: it namespaces every step's source_ref, so a retried log lands once.
  const [sequenceId, setSequenceId] = useState('');
  const phone = lead.mobile!;

  useEffect(() => { shareApi.kit().then(setKit).catch((e) => setError(errText(e))); }, []);

  const start = (planned: ShareStep[]) => {
    setSequenceId(crypto.randomUUID());
    setSteps(planned); setIndex(0); setPhase('ready'); setConfirmed([]);
  };

  const plan = async (req: Parameters<typeof shareApi.draft>[0]) => {
    setBusy(true); setError(null);
    try {
      const r = await shareApi.draft(req);
      if (!r.steps.length) throw new Error('Nothing to send');
      start(r.steps);
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  };

  // Link mode wherever the asset allows it: WhatsApp desktop can't be handed a
  // file, but a tracked link travels in the text.
  const selections = () => picked.map(({ kind, id }) => {
    if (kind === 'suggestion') return { id, kind };
    const a = kit!.assets.find((x) => x.id === id)!;
    return { id, kind, mode: (a.supports_link ? 'link' : 'file') as 'link' | 'file' };
  });
  const position = (kind: Pick['kind'], id: string) => {
    const i = picked.findIndex((p) => p.kind === kind && p.id === id);
    return i < 0 ? null : i + 1;
  };
  const toggle = (kind: Pick['kind'], id: string) => setPicked((p) => (
    p.some((x) => x.kind === kind && x.id === id) ? p.filter((x) => !(x.kind === kind && x.id === id)) : [...p, { kind, id }]
  ));
  // Every plan comes from the server, even a lone ready-made message: its
  // text is resolved per lead (the share link).
  const draft = (verbatim: boolean) => plan({
    lead_id: lead.id, operator_name: operatorName || undefined, operator_email: operatorEmail || undefined,
    hint: hint.trim() || undefined, assets: selections(), verbatim,
  });
  const playbook = (id: string) => plan({ lead_id: lead.id, playbook: id, operator_email: operatorEmail || undefined });

  const done = steps !== null && index >= steps.length;
  const step = steps && !done ? steps[index] : null;

  const edit = (text: string) => setSteps((ss) => ss!.map((s, i) => (i !== index ? s
    : s.kind === 'attachment' ? { ...s, caption: text } : { ...s, text })));

  const send = () => { openChat(phone, stepText(step!)); setPhase('awaiting'); };
  const next = (didSend: boolean) => {
    if (didSend) setConfirmed((c) => [...c, index]);
    setIndex((i) => i + 1); setPhase('ready');
  };
  const confirm = async () => {
    const s = step!;
    setPhase('confirming'); setError(null);
    try {
      await activityApi.append({
        lead_id: lead.id, kind: 'message', channel: 'whatsapp', direction: 'outbound',
        source_ref: `operator:wa_seq:${sequenceId}:s${index}`,
        summary: s.kind === 'attachment' ? `Personal WhatsApp: sent ${s.filename}` : `Personal WhatsApp: ${(s.text || '').slice(0, 140)}`,
        payload: {
          via: 'operator_personal_wa', sequence_id: sequenceId, step_index: index, total_steps: steps!.length,
          step_kind: s.kind, asset_id: s.asset_id ?? null, text: stepText(s),
        },
      });
      onLogged();
      next(true);
    } catch (e) {
      // The message really went; let them retry the log (the ref makes it safe).
      setError(errText(e)); setPhase('awaiting');
    }
  };

  // ── Picking ───────────────────────────────────────────────────────────────
  if (steps === null) {
    const byId = (kind: string, id: string) =>
      kind === 'asset' ? kit?.assets.find((a) => a.id === id)
        : kind === 'suggestion' ? kit?.suggestions.find((a) => a.id === id)
          : kit?.playbooks.find((a) => a.id === id);
    return (
      <Sheet title="Send on your WhatsApp" sub={`${lead.name} · ${lead.mobile}`} onClose={onClose} error={error}
        footer={<>
          <button className="btn" onClick={() => openChat(phone)}>Just open their chat</button>
          <button className="btn" disabled={busy || !hint.trim()} onClick={() => draft(true)}>Send as typed</button>
          <button className="btn btn-pri" disabled={busy || (!picked.length && !hint.trim())} onClick={() => draft(false)}>
            {busy ? 'Writing…' : picked.length > 1 && !hint.trim() ? `Send these · ${picked.length}` : picked.length && !hint.trim() ? 'Send this' : '✨ Write the message'}
          </button>
        </>}>
        <p className="text-[12.5px] text-graphite m-0">
          Each message opens WhatsApp on this computer, straight into their chat with the text already typed.
          Press send there, then come back and confirm. Confirmed messages go on the timeline.
        </p>
        {!kit && !error && <p className="text-sm text-graphite">Loading…</p>}
        {kit?.categories.map((cat) => (
          <div key={cat.id}>
            <SheetLabel>{cat.name}</SheetLabel>
            <div className="flex flex-wrap gap-1.5">
              {cat.items.map((it) => {
                const item: any = byId(it.kind, it.id);
                if (!item) return null;
                if (it.kind === 'asset' || it.kind === 'suggestion') {
                  const kind: Pick['kind'] = it.kind;
                  const n = position(kind, it.id);
                  return (
                    <button key={it.id} type="button" className="pill" aria-pressed={n !== null}
                      title={kind === 'asset' ? item.caption : item.text} onClick={() => toggle(kind, it.id)}>
                      {n !== null && (
                        <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-forest text-white text-[11px] font-bold mr-1">{n}</span>
                      )}
                      {it.kind === 'asset' ? '📎 ' : '💬 '}{item.label}
                    </button>
                  );
                }
                return (
                  <button key={it.id} type="button" className="pill !border-forest/40" disabled={busy}
                    onClick={() => playbook(it.id)}>
                    ▶ {item.label} · {item.step_count}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div>
          <SheetLabel>Your message or a steer</SheetLabel>
          <textarea id="share-hint" className="inp w-full" rows={3} value={hint} onChange={(e) => setHint(e.target.value)}
            placeholder="e.g. remind him about Sunday's visit and share the drive route" />
        </div>
      </Sheet>
    );
  }

  // ── Sending ───────────────────────────────────────────────────────────────
  return (
    <Sheet title="Send on your WhatsApp" sub={`${lead.name} · ${lead.mobile}`} onClose={onClose} error={error}
      footer={done ? <button className="btn btn-pri" onClick={onClose}>Close</button>
        : phase === 'ready' ? <>
          <button className="btn" onClick={() => setIndex(steps.length)}>Stop</button>
          <button className="btn" onClick={() => next(false)}>Skip</button>
          <button className="btn btn-pri !bg-[#128c7e] !border-[#128c7e]" onClick={send}>Open in WhatsApp</button>
        </> : <>
          <button className="btn" disabled={phase === 'confirming'} onClick={() => next(false)}>Skip this one</button>
          <button className="btn" disabled={phase === 'confirming'} onClick={() => setPhase('ready')}>No, open again</button>
          <button className="btn btn-pri" disabled={phase === 'confirming'} onClick={confirm}>
            {phase === 'confirming' ? 'Logging…' : index === steps.length - 1 ? 'Yes, sent' : 'Yes, next'}
          </button>
        </>}>
      {done ? (
        <div className="bg-cream border-[1.5px] border-sketch rounded-lg p-4 text-center">
          <b className="text-[15px]">{confirmed.length === steps.length ? `All ${steps.length} sent` : `${confirmed.length} of ${steps.length} sent`}</b>
          <p className="text-[12.5px] text-graphite m-0 mt-1">Confirmed messages are on the timeline.</p>
        </div>
      ) : step && (
        <>
          <SheetLabel>Message {index + 1} of {steps.length}</SheetLabel>
          {step.kind === 'attachment' && (
            <div className="bg-cream border-[1.5px] border-sketch rounded-lg p-3 text-[12.5px]">
              📎 {step.filename} goes with this message. WhatsApp desktop can't take it from here:{' '}
              <a href={step.url || '#'} target="_blank" rel="noreferrer" className="text-forest underline font-semibold">download it</a>,
              then drag it into the chat before you send.
            </div>
          )}
          <textarea id="share-step" className="inp w-full" rows={7} value={stepText(step)} disabled={phase !== 'ready'}
            onChange={(e) => edit(e.target.value)} />
          {phase !== 'ready' && (
            <div className="bg-gold/10 border-[1.5px] border-gold/40 rounded-lg p-3 text-[13px]">
              <b>Did message {index + 1} go?</b>
              <p className="m-0 text-graphite text-[12.5px]">Only confirm once you've pressed send in WhatsApp.</p>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
};

export default ShareSheet;
